import { useState } from 'react';
import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@tigergraph/app-ui-lib/modal';
import { Button } from '@tigergraph/app-ui-lib/button';
import { Select } from '@tigergraph/app-ui-lib/select';
import { Checkbox } from '@tigergraph/app-ui-lib/checkbox';
import { OnChangeParams } from 'baseui/select';
import { getReleaseNotesUrl, TGVersion, WorkspaceT } from '@/pages/workgroup/type';
import { AiOutlineQuestionCircle } from 'react-icons/ai';
import { StatefulPopover } from '@tigergraph/app-ui-lib/popover';
import { Check } from 'baseui/icon';
import { StyledToast } from '@tigergraph/app-ui-lib/styledToasterContainer';
import { ALIGN, RadioGroup } from 'baseui/radio';
import { Radio } from '@tigergraph/app-ui-lib/radio';
import mediaQuery from '@/lib/breakpoints';
import { compare } from 'compare-versions';

interface UpgradeVersionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (payload: { version: string; timing: 'now' | 'schedule' }) => void;
  versions: TGVersion[];
  desiredTgVersion?: string;
  defaultTiming?: 'now' | 'schedule';
  workspace?: WorkspaceT;
}

export default function UpgradeVersionModal({
  isOpen,
  onClose,
  onConfirm,
  versions,
  desiredTgVersion,
  defaultTiming,
  workspace,
}: UpgradeVersionModalProps) {
  const [css, theme] = useStyletron();

  // Determine current workspace version for filtering
  const currentVersion = workspace?.tg_version || '';
  const filteredVersions = currentVersion
    ? versions.filter((v) => {
        if (!compare(v.name, currentVersion, '>') || !v.allow_upgrade) return false;
        return !v.incompatible_upgrades_from.some((pattern) => {
          const regex = new RegExp('^' + pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\\\*/g, '.*') + '$');
          return regex.test(currentVersion);
        });
      })
    : versions;

  const [currentSelectedVersion, setCurrentSelectedVersion] = useState<string>(() => {
    if (filteredVersions.length === 0) {
      return '';
    }
    if (!desiredTgVersion || desiredTgVersion === 'auto') {
      const ltsVersions = filteredVersions.filter((v) => v.channel === 'lts');
      if (ltsVersions.length > 0) {
        return ltsVersions[ltsVersions.length - 1].name;
      }
      return filteredVersions[filteredVersions.length - 1].name;
    }
    // Only pre-select desiredTgVersion if it is actually a valid upgrade option
    const isValidUpgrade = filteredVersions.some((v) => v.name === desiredTgVersion);
    return isValidUpgrade ? desiredTgVersion : '';
  });
  const [isAcknowledged, setIsAcknowledged] = useState(false);
  const [isPreviewVersionConfirmed, setIsPreviewVersionConfirmed] = useState(false);
  const [timing, setTiming] = useState<'now' | 'schedule'>(defaultTiming || 'now');
  const [showWarning, setShowWarning] = useState(false);

  // Create version options with additional information
  const versionOptions = filteredVersions.map((v) => ({
    id: v.name,
    label: v.name,
    version: v,
    isPreview: v.channel === 'preview',
  }));

  const getVersionLabel = ({ option, isValue }: { option?: any; isValue?: boolean }) => {
    if (!option) return null;

    const isPreview = option.isPreview;
    const isSelected = currentSelectedVersion === option.id;

    return (
      <div
        className={css({
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          gap: '8px',
        })}
      >
        <span>TigerGraph {option.label}</span>
        {isPreview && (
          <StatefulPopover content="Not recommended for production use" triggerType="hover">
            <div
              className={css({
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                backgroundColor: theme.colors['tag.background.default'],
                borderRadius: '20px',
                padding: '2px 12px',
                fontSize: '12px',
                lineHeight: '16px',
              })}
            >
              <span>Preview</span>
              <AiOutlineQuestionCircle size={14} color={theme.colors['text.secondary']} />
            </div>
          </StatefulPopover>
        )}
        {isSelected && !isValue && <Check size={20} color={theme.colors['icon.primary']} />}
      </div>
    );
  };

  const handleVersionChange = (params: OnChangeParams) => {
    const newVersion = (params.value[0]?.id as string) || '';
    setCurrentSelectedVersion(newVersion);
    // Reset acknowledgment when version changes
    setIsAcknowledged(false);
    setIsPreviewVersionConfirmed(false);
  };

  const handleConfirm = () => {
    const selectedOption = versionOptions.find((v) => v.id === currentSelectedVersion);
    const isPreview = selectedOption?.isPreview || false;

    if (currentSelectedVersion && isAcknowledged && (!isPreview || isPreviewVersionConfirmed)) {
      setShowWarning(true);
    }
  };

  const handleWarningConfirm = () => {
    setShowWarning(false);
    onConfirm({ version: currentSelectedVersion, timing });
  };

  const handleClose = () => {
    const isValidUpgrade = filteredVersions.some((v) => v.name === desiredTgVersion);
    setCurrentSelectedVersion(isValidUpgrade ? desiredTgVersion! : '');
    setIsAcknowledged(false);
    setIsPreviewVersionConfirmed(false);
    setTiming(defaultTiming || 'now');
    setShowWarning(false);
    onClose();
  };

  const selectedVersionOption = versionOptions.find((v) => v.id === currentSelectedVersion);
  const isSelectedVersionPreview = selectedVersionOption?.isPreview || false;
  const hasUpgradeVersions = filteredVersions.length > 0;
  const canConfirm =
    hasUpgradeVersions &&
    currentSelectedVersion &&
    selectedVersionOption !== undefined &&
    isAcknowledged &&
    (!isSelectedVersionPreview || isPreviewVersionConfirmed);

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        handleClose();
      }}
      // @ts-ignore
      size="min(620px, 90%)"
    >
      <ModalHeader>
        <div
          className={css({
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          })}
        >
          <div>
            <div
              className={css({
                fontSize: '20px',
                fontWeight: 600,
                color: theme.colors['text.primary'],
                marginBottom: '4px',
              })}
            >
              Select Version To Upgrade
            </div>
            <div
              className={css({
                fontSize: '14px',
                color: theme.colors['text.secondary'],
                lineHeight: '16px',
              })}
            >
              Please select the version you want to upgrade this workspace to.
            </div>
          </div>
        </div>
      </ModalHeader>

      <ModalBody>
        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
          })}
        >
          {/* Version Selection */}
          <div
            className={css({
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            })}
          >
            <div
              className={css({
                fontWeight: 500,
              })}
            >
              Select Version
            </div>
            {!hasUpgradeVersions ? (
              <div
                className={css({
                  padding: '12px 16px',
                  backgroundColor: theme.colors['background.secondary'],
                  borderRadius: '4px',
                  color: theme.colors['text.secondary'],
                  fontSize: '14px',
                })}
              >
                This workspace is already running the latest available version. No upgrade is available at this time.
              </div>
            ) : (
              <>
                <div
                  className={css({
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                  })}
                >
                  <div
                    className={css({
                      width: '100%',
                    })}
                  >
                    <Select
                      value={currentSelectedVersion ? [{ id: currentSelectedVersion }] : []}
                      onChange={handleVersionChange}
                      options={versionOptions}
                      placeholder="Select a version"
                      clearable={false}
                      backspaceClearsInputValue={false}
                      backspaceRemoves={false}
                      getOptionLabel={getVersionLabel}
                      getValueLabel={({ option }) => getVersionLabel({ option, isValue: true })}
                    />
                  </div>
                </div>
                {currentSelectedVersion && (
                  <a
                    className={css({ color: theme.colors.linkText })}
                    href={getReleaseNotesUrl(currentSelectedVersion)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    View Release Notes
                  </a>
                )}
              </>
            )}
          </div>

          {/* Acknowledgements */}
          {hasUpgradeVersions && (
            <div
              className={css({
                padding: '16px',
                backgroundColor: theme.colors['background.secondary'],
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
              })}
            >
              {isSelectedVersionPreview && (
                <Checkbox
                  checked={isPreviewVersionConfirmed}
                  onChange={(e) => setIsPreviewVersionConfirmed((e.target as HTMLInputElement).checked)}
                >
                  Preview Versions may include breaking changes that could affect your workspace.
                </Checkbox>
              )}

              <Checkbox
                checked={isAcknowledged}
                onChange={(e) => setIsAcknowledged((e.target as HTMLInputElement).checked)}
              >
                I acknowledge and confirm this version selection.
              </Checkbox>
            </div>
          )}

          {/* Upgrade Timing */}
          <div
            className={css({
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            })}
          >
            <div className={css({ fontWeight: 500 })}>Upgrade Timing</div>
            <RadioGroup
              value={timing}
              onChange={(e) => setTiming((e.currentTarget.value as 'now' | 'schedule') || defaultTiming || 'now')}
              align={ALIGN.horizontal}
              overrides={{
                RadioGroupRoot: {
                  style: {
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr',
                    gap: '64px',
                    [mediaQuery.xsmall]: {
                      gap: '16px',
                    },
                  },
                },
              }}
            >
              <Radio value="now">Upgrade Now</Radio>
              <Radio value="schedule">Schedule During Maintenance Window</Radio>
            </RadioGroup>
            {timing === 'schedule' && (
              <StyledToast
                message="If this scheduled upgrade overlaps with a Maintenance upgrade, the Feature/Preview upgrade will take precedence."
                hideBorder={true}
                closeable={false}
              />
            )}
          </div>
        </div>
      </ModalBody>

      <ModalFooter>
        <div
          className={css({
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            width: '100%',
          })}
        >
          <Button kind="secondary" onClick={handleClose} size="large">
            Cancel
          </Button>
          <Button kind="primary" onClick={handleConfirm} disabled={!canConfirm} size="large">
            Confirm
          </Button>
        </div>
      </ModalFooter>

      {showWarning && (
        <Modal
          isOpen
          onClose={() => setShowWarning(false)}
          // @ts-ignore
          size="min(520px, 90%)"
        >
          <ModalHeader>
            <div className={css({ fontSize: '18px', fontWeight: 600, color: theme.colors['text.primary'] })}>
              Warning
            </div>
          </ModalHeader>
          <ModalBody>
            <div>
              {timing === 'now'
                ? 'Upgrading this workspace will temporarily make it unavailable for other operations. Please ensure you have saved your work and completed all necessary tasks before continuing.'
                : `You’ve scheduled a manual upgrade for TigerGraph ${currentSelectedVersion} during the next maintenance window. During this time, the workspace will be temporarily unavailable. Do you want to proceed?`}
            </div>
          </ModalBody>
          <ModalFooter>
            <div className={css({ display: 'flex', justifyContent: 'flex-end', gap: '12px', width: '100%' })}>
              <Button kind="secondary" size="large" onClick={() => setShowWarning(false)}>
                Cancel
              </Button>
              <Button kind="primary" size="large" onClick={handleWarningConfirm}>
                Confirm
              </Button>
            </div>
          </ModalFooter>
        </Modal>
      )}
    </Modal>
  );
}
